/**
 * A paper field offered as two choices, type then weight, instead of one long
 * list of "Silk 350gsm, Silk 400gsm, Uncoated 350gsm…" (Chris, 24 Oct 2026, R204).
 *
 * The backend reads each option's `{class}-{gsm}` code back into its type and
 * weight (`paper`); this only groups them. A named stock (Arena Smooth, a
 * customer's own paper) has no `paper`, so it is a type of its own with no
 * weight to pick; a catalogue that wants only that stock pins the field to it.
 * The selection is still the one option code, so pricing, `requires` and the
 * saved spec are unchanged.
 */

import type { PaperStock } from "../data/catalogue-types";

export interface PaperOptionLike {
  code: string;
  /** A named stock's whole name, which stands as its type. */
  label: string;
  isDefault?: boolean;
  paper?: PaperStock | null;
}

export interface PaperTypeChoice<T extends PaperOptionLike = PaperOptionLike> {
  code: string;
  label: string;
  /** This type's options, lightest first: the only weights offered once it is chosen. */
  options: T[];
  /** A named stock: one option, no weight to choose. */
  isNamedStock: boolean;
}

export interface PaperSplit<T extends PaperOptionLike = PaperOptionLike> {
  types: PaperTypeChoice<T>[];
  /** The type the selected option is; undefined while no option is selected (or a custom value is). */
  selectedType: PaperTypeChoice<T> | undefined;
}

function typeCodeOf(option: PaperOptionLike): string {
  return option.paper?.typeCode ?? option.code;
}

/**
 * The field's options as paper types and their weights, in the order the
 * dictionary lists them, a named stock a type of its own; or null when the
 * field stays one choice: fewer than two paper types (weight is the only
 * choice, or it is no paper field), or no paper type offering more than one
 * weight (the type is the only choice).
 */
export function paperSplit<T extends PaperOptionLike>(
  options: readonly T[],
  selectedCode: string | undefined,
): PaperSplit<T> | null {
  const byCode = new Map<string, PaperTypeChoice<T>>();
  for (const option of options) {
    const code = typeCodeOf(option);
    const type = byCode.get(code) ?? {
      code,
      label: option.paper?.typeLabel ?? option.label,
      options: [],
      isNamedStock: !option.paper,
    };
    type.options.push(option);
    byCode.set(code, type);
  }
  const types = [...byCode.values()];
  const paperTypes = types.filter((t) => !t.isNamedStock);
  if (paperTypes.length < 2 || !paperTypes.some((t) => t.options.length > 1)) {
    return null;
  }
  for (const type of paperTypes) {
    type.options.sort((a, b) => a.paper!.weight - b.paper!.weight);
  }
  const selected = options.find((o) => o.code === selectedCode);
  return {
    types,
    selectedType: selected ? byCode.get(typeCodeOf(selected)) : undefined,
  };
}

/**
 * The option to select when the orderer picks a paper type: the weight they
 * had, if that type offers it; else the type's default; else its lightest.
 * An option the rest of the selection rules out (R28) is passed over while the
 * type has another.
 */
export function optionForPaperType<T extends PaperOptionLike>(
  type: PaperTypeChoice<T>,
  current: PaperOptionLike | undefined,
  unavailableCodes?: ReadonlySet<string>,
): T {
  const available = type.options.filter((o) => !unavailableCodes?.has(o.code));
  const candidates = available.length > 0 ? available : type.options;
  const weight = current?.paper?.weight;
  return (
    candidates.find(
      (o) => weight !== undefined && o.paper?.weight === weight,
    ) ??
    candidates.find((o) => o.isDefault) ??
    candidates[0]
  );
}

/** Whether every weight of a type is ruled out by the rest of the selection, so the type itself is. */
export function isPaperTypeUnavailable(
  type: PaperTypeChoice,
  unavailableCodes: ReadonlySet<string> | undefined,
): boolean {
  return type.options.every((o) => unavailableCodes?.has(o.code) ?? false);
}
